import { useTranslation } from 'react-i18next';
import { useGetTraceabilityQuery } from '@/redux/internal-services/notesApi';
import { IconLink, IconMic, IconNote } from '@/components/notes/icons';

interface Props {
  noteId: string;
}

/**
 * Traceability (screen G) — full lineage of a note: upstream (capture source +
 * business context), downstream (records created from it), the activity timeline,
 * and the AI analysis provenance. Read-only; assembled server-side.
 */
export function NoteTraceability({ noteId }: Props) {
  const { t } = useTranslation();
  const { data, isLoading } = useGetTraceabilityQuery(noteId);

  if (isLoading || !data) {
    return <p className="text-sm text-ui-text3">{t('noteTaker.loading')}</p>;
  }

  const { upstream, downstream, timeline } = data;

  return (
    <div className="flex flex-col gap-4 text-sm">
      {/* Upstream */}
      <section>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ui-text3">{t('noteTaker.capturedFrom', 'Captured from')}</p>
        <div className="flex flex-col gap-1.5 rounded-xl border border-ui-border bg-ui-subtle p-3">
          <div className="flex items-center gap-2">
            {upstream.source === 'voice' ? <IconMic className="h-4 w-4" /> : <IconNote className="h-4 w-4" />}
            <span className="text-ui-text">{t(`noteTaker.tabs.${upstream.source === 'voice' ? 'voice' : 'text'}`, upstream.source || 'text')}</span>
          </div>
          {upstream.context && (
            <div className="flex items-center gap-2 text-ui-text3">
              <IconLink className="h-4 w-4" />
              <span>
                {t(`noteTaker.contexts.${upstream.context.context_type}`, upstream.context.context_type)}
                {upstream.context.display_label ? ` · ${upstream.context.display_label}` : ''}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Downstream */}
      <section>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ui-text3">{t('noteTaker.recordsCreated', 'Records created from this note')}</p>
        {downstream.length === 0 ? (
          <p className="text-xs text-ui-text3">{t('noteTaker.noRecords', 'No records created yet.')}</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {downstream.map((rec, i) => {
              const r = rec as Record<string, unknown>;
              return (
                <div key={i} className="flex items-center justify-between gap-2 rounded-xl border border-ui-border bg-ui-subtle p-3">
                  <div className="min-w-0">
                    <p className="truncate text-ui-text">{String(r.label ?? r.ref_id ?? '—')}</p>
                    <p className="text-xs text-ui-text3">{String(r.target ?? r.ref_type ?? '')}</p>
                  </div>
                  <span className="rounded-full bg-success/15 px-2 py-0.5 text-xs font-semibold text-success">
                    {t('noteTaker.created', 'Created')}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Activity timeline */}
      <section>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ui-text3">{t('noteTaker.activity', 'Activity')}</p>
        <ul className="flex flex-col gap-2">
          {timeline.map((ev, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden />
              <div className="min-w-0">
                <p className="text-ui-text">{t(`noteTaker.activityType.${ev.activity_type}`, ev.activity_type.replace(/_/g, ' '))}</p>
                <p className="text-xs text-ui-text3">
                  {ev.actor_name ? `${ev.actor_name} · ` : ''}
                  {new Date(ev.occurred_at).toLocaleString()}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export default NoteTraceability;
